import { APP_INITIALIZER, EventEmitter, inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { BAAS_CUSTOM_EVENT, BaasAppState, BaasBasiscontext, openUrl, resolveValue } from '@lvflanders/bar';

import { BarActionDef } from '../models/bar-action-def.type';
import { BarRoutingService } from './bar-routing.service';

@Injectable({ providedIn: 'root' })
export class BarCoreService implements OnDestroy {
    readonly #injector = inject(Injector);
    readonly #appState = inject(BaasAppState, { optional: true });

    /** Global core event that is invoked when an error occurs. */
    readonly errorOccurred = new EventEmitter<Error>();
    /** Event invoked when performed soft refresh. */
    readonly refreshed = new EventEmitter<void>();
    /** Occurs when a custom message is send from the BaaS hub and received in the client. */
    readonly customHubMessageReceived = new EventEmitter<any>();
    readonly basiscontextChanged = new EventEmitter<BaasBasiscontext>();

    /** Action to perform when the application navigates to home. Use this to override the default behaviour. */
    homeAction: BarActionDef;
    /** Action to perform when the application navigates to the root/start of application. Use this to override the default behaviour. */
    rootAction: BarActionDef;

    readonly #hubMessage = (event: any) => this.customHubMessageReceived.emit(event.detail);
    readonly #basiscontextChanged = (event: any) => this.basiscontextChanged.emit(event.detail);

    // avoid cyclic dependency
    get #router(): Router {
        return this.#injector.get(Router);
    }

    get #routing(): BarRoutingService {
        return this.#injector.get(BarRoutingService);
    }
    // avoid cyclic dependency
    get #appInits(): (() => any)[] {
        return this.#injector.get<(() => any)[]>(APP_INITIALIZER);
    }

    constructor() {
        // Listen for the custom event on a JavaScript object or DOM element
        window.addEventListener(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, this.#hubMessage);
        window.addEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChanged);
    }

    ngOnDestroy(): void {
        window.removeEventListener(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, this.#hubMessage);
        window.removeEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChanged);
        this.basiscontextChanged.complete();
        this.customHubMessageReceived.complete();
        this.errorOccurred.complete();
        this.refreshed.complete();
    }

    /** Performs a soft refresh (make sure to have onSameUrlNavigation set to 'reload' in the routing setup) */
    async refresh(): Promise<void> {
        // https://dormoshe.io/articles/hook-into-angular-initialization-process-8
        // get all async promises (or executed if no promise)
        if (this.#appInits) {
            for (const appInit of this.#appInits) {
                const initResult = appInit();
                await Promise.resolve(initResult);
            }
        }
        // after executing all startup logic
        // save url before navigating (otherwise wrong one)
        const url = this.#router.url;
        // use custom navigate with to reload to ensure route component is completely reloaded
        await this.#routing.navigateWithReload([url]);
        this.refreshed.emit();
    }

    /**
     * Navigate to the defined home url (default is startscherm)
     * @param invokeHomeAction Whether to invoke the custom home action defined in this service as a property.
     */
    async navigateHome(invokeHomeAction = true): Promise<void> {
        await this.#navigateAction(
            invokeHomeAction,
            () => {
                window.location = this.#appState.config.homeUrl;
            },
            this.homeAction,
            undefined
        );
    }

    async navigateRoot(invokeRootAction = true, event: Event = undefined): Promise<void> {
        await this.#navigateAction(
            invokeRootAction,
            async () => {
                const newTab = event && ((<any>event).ctrlKey || (<any>event).metaKey);
                if (newTab) {
                    openUrl(this.#appState.location.app.url, '_blank');
                } else {
                    await this.#router.navigate(['./']);
                }
            },
            this.rootAction,
            event
        );
    }

    async #navigateAction(invokeAction: boolean, defaultAction: () => void, action: BarActionDef, event: Event): Promise<void> {
        let defaultBehaviour = false;

        if (invokeAction && action != null) {
            if (action.action != null) {
                const result = <any>action.action(event);
                if (result instanceof Promise) {
                    await (<Promise<void>>result);
                }
            } else if (action.routerUrl != null) {
                const routerUrl = resolveValue(action.routerUrl);
                await this.#router.navigateByUrl(routerUrl);
            } else if (action.routerCommands != null) {
                const routerLink = resolveValue(action.routerCommands);
                await this.#router.navigate(routerLink);
            } else if (action.href != null) {
                const href = resolveValue(action.href);
                openUrl(href, action.target);
            } else {
                defaultBehaviour = true;
            }
        } else {
            defaultBehaviour = true;
        }

        if (defaultBehaviour) defaultAction();
    }
}
